import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBasket, Heart, User, Menu, X, Globe, LayoutDashboard } from "lucide-react";
import { useLang } from "../i18n";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { count, wishlist, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/shop", label: t("nav.shop"), testid: "nav-shop-link" },
    { to: "/#manifesto", label: t("nav.manifesto"), testid: "nav-manifesto-link" },
    { to: "/corsi", label: t("nav.courses"), testid: "nav-courses-link" },
    { to: "/chi-siamo", label: t("nav.about"), testid: "nav-about-link" },
  ];

  const goManifesto = (e, to) => {
    if (to.includes("#")) {
      e.preventDefault();
      const el = document.getElementById("manifesto");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else navigate("/");
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-parchment/80 backdrop-blur-md backdrop-saturate-150 border-b border-[#E5D9C8]/70" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo.png" alt="LoMIELlina" className="h-11 w-11 rounded-full shadow-md shadow-amber-900/20" />
          <span className="font-serif text-2xl font-bold tracking-tight">Lo<span className="text-honey">MIEL</span>lina</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={(e) => goManifesto(e, l.to)} data-testid={l.testid}
              className="text-cocoa/70 hover:text-honey transition-colors duration-200 font-medium">
              {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link" className="flex items-center gap-1.5 text-terra hover:text-honey transition-colors font-medium">
              <LayoutDashboard size={15} /> {t("nav.admin")}
            </Link>
          )}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <button onClick={() => setLang(lang === "it" ? "en" : "it")} data-testid="language-toggle"
            className="flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-cocoa/60 hover:text-honey transition-colors px-2 py-1">
            <Globe size={14} /> {lang === "it" ? "EN" : "IT"}
          </button>
          <Link to="/account" data-testid="nav-wishlist-btn" className="relative p-2 text-cocoa/70 hover:text-honey transition-colors">
            <Heart size={19} />
            {wishlist.length > 0 && (
              <span data-testid="wishlist-count-badge" className="absolute -top-0.5 -right-0.5 bg-terra text-parchment text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>
            )}
          </Link>
          <button onClick={() => setDrawerOpen(true)} data-testid="cart-open-btn" className="relative p-2 text-cocoa/70 hover:text-honey transition-colors">
            <ShoppingBasket size={19} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-honey text-parchment text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
            )}
          </button>
          <Link to={user ? "/account" : "/accedi"} data-testid="nav-account-btn" className="hidden sm:flex p-2 text-cocoa/70 hover:text-honey transition-colors">
            <User size={19} />
          </Link>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="lg:hidden p-2 text-cocoa/70">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-parchment/95 backdrop-blur-md border-t border-[#E5D9C8]" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={(e) => { goManifesto(e, l.to); setOpen(false); }} data-testid={`mobile-${l.testid}`}
                  className="text-cocoa/80 font-medium py-1">{l.label}</Link>
              ))}
              <Link to={user ? "/account" : "/accedi"} onClick={() => setOpen(false)} data-testid="mobile-nav-account-btn" className="text-cocoa/80 font-medium py-1">
                {user ? t("nav.account") : t("nav.login")}
              </Link>
              {user?.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin-link" className="text-terra font-medium py-1">{t("nav.admin")}</Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
